/**
 * select_item — product card clicks on listing surfaces. (#157)
 *
 * The join between a listing and a PDP visit. `view_item_list` (#156) says a product was seen and
 * `view_item` says a product page was opened, but nothing today says WHICH listing sent the visitor
 * there — so PDP traffic and downstream revenue can't be attributed back to a collection, carousel or
 * search query. This is the event that closes that gap.
 *
 * Contract, emitted by Liquid — identical to the one `analytics-impressions.js` reads, on purpose:
 *   container  [data-gtm-list-id]      required — e.g. "collection_fresh-fruits"
 *              [data-gtm-list-name]    required — e.g. "Fresh Fruits"
 *              [data-gtm-list-offset]  optional — paginate.current_offset, so page 2 starts at 25
 *   card       [data-gtm-item]         required — one GA4 item object, HTML-escaped JSON,
 *                                       built by snippets/analytics-item.liquid
 *
 * 🔴 `item_list_id` / `item_list_name` are read from the SAME container attributes that
 * `view_item_list` reads. They can't drift, which is what the funnel join depends on: a
 * select_item whose list id doesn't match the preceding view_item_list is unqueryable.
 *
 * WHAT COUNTS AS A SELECTION
 * A click that navigates to the product — the image link, the title link, a colour swatch, "quick
 * view", "view options". Not the whole card indiscriminately:
 *   - quick-add ("Add") is `add_to_cart` (#163), not a selection — the ticket is explicit;
 *   - the variant `<select>`, the quantity stepper and the wishlist toggle select nothing;
 *   - dead space (the price text, card padding) navigates nowhere, so firing there would invent a
 *     funnel step that never happened and inflate select_item past PDP views — which the acceptance
 *     criteria specifically forbid ("never wildly exceed").
 * The classifier below walks OUT from the clicked node, so the innermost thing wins. That matters
 * here: `product-card-item-v2.liquid:145` renders `<a class="add-to-cart-button" href="{{ product.url }}">
 * View options</a>` — a link wearing the add-to-cart class. Excluding by class name would drop a real
 * navigation; excluding by "is it actually a control" keeps it.
 *
 * NOT LOSING THE EVENT ON NAVIGATION
 * The listener is registered in the CAPTURE phase on `document`, so the push happens before the theme's
 * own handlers run (`snippets/om-cards-runtime.liquid:155` calls `stopPropagation()` on quick-add) and
 * before the browser starts unloading. GTM's GA4 tag then sends the hit with `navigator.sendBeacon`,
 * which survives unload. DebugView under rapid clicking is the verification gate — see
 * docs/GA4-DEBUGVIEW-CHECKLIST.md. We deliberately do NOT `preventDefault()` and re-navigate on a
 * callback: that trades a measurement edge case for a slower, breakable click on every product in the
 * store.
 *
 * Delegated from `document`, so cards injected by AJAX filtering, load-more and Swiper are covered with
 * no re-binding.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var CARD = '[data-gtm-item]';
  var LIST = '[data-gtm-list-id]';

  // A repeat click on the same card inside this window is one selection, not two. A double-click on a
  // link navigates once and produces one view_item; counting it twice would break the plausibility
  // check of select_item against PDP views.
  var REPEAT_MS = 1000;

  // Elements that are interactive but select nothing. `.product-wishlist` is a <span>, and the
  // quantity stepper is a pair of buttons, so tag names alone aren't enough.
  var CONTROL_TAGS = { BUTTON: 1, INPUT: 1, SELECT: 1, TEXTAREA: 1, LABEL: 1, SUMMARY: 1, OPTION: 1 };
  // `[data-coop-join]` is the one <a> on a card that is not a product link: the price-free "Join the
  // Co-Op" pill om-card-2026 renders where a product is in neither Co-Op tier. It leaves for
  // /pages/membership, so isNavigation() would otherwise read it as selecting the product.
  var CONTROL_SELECTOR = '[data-variant-add], [data-fp-add], [role="button"], .product-wishlist, .om-qs, [data-coop-join]';

  var lastCard = null;
  var lastAt = 0;

  function matches(el, selector) {
    var fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    return fn ? fn.call(el, selector) : false;
  }

  function isControl(el) {
    if (CONTROL_TAGS[el.tagName]) return true;
    return matches(el, CONTROL_SELECTOR);
  }

  function isNavigation(el) {
    if (el.tagName !== 'A') return false;
    var href = el.getAttribute('href');
    if (!href) return false;
    href = href.trim();
    // `#` toggles something on the page; `javascript:` runs something. Neither leaves for the product.
    return href !== '' && href.charAt(0) !== '#' && href.slice(0, 11).toLowerCase() !== 'javascript:';
  }

  /**
   * Does this click select the product, or is it something else that happens to live on the card?
   * Walks out from the clicked node to the card so the INNERMOST match decides.
   */
  function selectsProduct(target, card) {
    for (var el = target; el && el !== card; el = el.parentElement) {
      if (el.nodeType !== 1) continue;
      if (isControl(el)) return false;
      if (isNavigation(el)) return true;
    }
    return false; // dead space on the card — navigates nowhere, so it is not a selection
  }

  /**
   * True 1-based position of `el` among the cards in `container`, plus any pagination offset.
   *
   * 🔴 Must stay identical to `positionOf` in `assets/analytics-impressions.js`. The two events are
   * only joinable if a card's `index` is the same in both, and this theme has no bundler to share a
   * module through. The test suite asserts both files return the same index for the same DOM.
   *
   * Swiper 8 runs the homepage carousels with `loop: true`, which CLONES slides into the same
   * `.swiper-wrapper` — including their `data-gtm-item`. Clones are excluded from the position count
   * (they'd shift every index), but a click on a clone is a real click, so it resolves to the original
   * card's position by matching the payload the clone was copied from.
   */
  function positionOf(container, el) {
    var all = container.querySelectorAll(CARD);
    var offset = parseInt(container.getAttribute('data-gtm-list-offset'), 10);
    if (isNaN(offset) || offset < 0) offset = 0;

    var real = [];
    for (var i = 0; i < all.length; i++) {
      if (!matches(all[i], '.swiper-slide-duplicate')) real.push(all[i]);
    }

    for (var j = 0; j < real.length; j++) {
      if (real[j] === el) return offset + j + 1;
    }

    var payload = el.getAttribute('data-gtm-item');
    for (var k = 0; k < real.length; k++) {
      if (real[k].getAttribute('data-gtm-item') === payload) return offset + k + 1;
    }
    return offset + 1;
  }

  function handle(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var card = target.closest(CARD);
    if (!card) return;

    var container = card.closest(LIST);
    // No list context means no `item_list_id`, and an event without one can't be joined to the
    // impression it came from. A half-populated event is worse than none — it looks like data.
    if (!container) return;

    if (!selectsProduct(target, card)) return;

    var now = event.timeStamp || 0;
    if (card === lastCard && now - lastAt < REPEAT_MS) return;
    lastCard = card;
    lastAt = now;

    var item;
    try {
      item = JSON.parse(card.getAttribute('data-gtm-item'));
    } catch (e) {
      return; // a malformed payload on one card must not throw inside a click that has to navigate
    }
    if (!item || !item.item_id) return;

    var listId = container.getAttribute('data-gtm-list-id');
    var listName = container.getAttribute('data-gtm-list-name') || '';

    item.index = positionOf(container, card);
    item.item_list_id = listId;
    item.item_list_name = listName;

    // Clear the previous ecommerce object first. GA4 merges dataLayer state, so without this the next
    // event can inherit items[] from this one.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_item',
      ecommerce: { item_list_id: listId, item_list_name: listName, items: [item] }
    });
  }

  // Capture phase: ahead of the theme's own click handlers, and ahead of navigation.
  document.addEventListener('click', handle, true);

  // Middle-click opens the PDP in a new tab and fires `auxclick`, not `click`. That visit produces a
  // view_item, so it needs its select_item too or the funnel loses the pair.
  document.addEventListener('auxclick', function (event) {
    if (event.button === 1) handle(event);
  }, true);
})();
